// src/pages/SystemLogs.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Avatar,
    Alert,
    Snackbar,
    LinearProgress,
    Card,
    CardContent
} from '@mui/material';
import {
    Delete,
    Search,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    CalendarMonth,
    Description,
    History,
    Computer,
    Info,
    CleaningServices,
    Storage,
    Add,
    Edit,
    Login,
    Logout,
    Visibility,
    Download,
    PrintOutlined,
    DeleteSweep,
    AccessTime
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APISystemLogsList,
    APISystemLogsStats,
    APISystemLogsDelete,
    APISystemLogsClean,
    APISystemLogsExportExcel,
    APISystemLogsExportPDF,
    APISystemLogsFilterOptions
} from '../../apiServices/APIs';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SystemLogs = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    const [pageState, setPageState] = useState({
        logsData: [],
        statistics: null,
        filterOptions: { models: [], actions: [] }
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [filters, setFilters] = useState({
        model: '',
        action: '',
        dateFrom: '',
        dateTo: ''
    });

    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openCleanDialog, setOpenCleanDialog] = useState(false);
    const [cleanDays, setCleanDays] = useState(30);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchStatistics();
        fetchFilterOptions();
    }, [showToast]);

    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, filters, searchTerm]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const api = apiService.getApi();
            const userBranchId = getUserDetails("branchId");
            const currentUserId = getUserDetails("Id");

            if (!currentUserId) {
                showToast("Error", "User not authenticated. Please log in again.", "error");
                setLoading(false);
                return;
            }

            const params = {
                page: page + 1,
                per_page: rowsPerPage,
                user_id: currentUserId
            };

            if (userBranchId) params.branch_id = userBranchId;
            if (filters.model) params.model = filters.model;
            if (filters.action) params.action = filters.action;
            if (filters.dateFrom) params.date_from = filters.dateFrom;
            if (filters.dateTo) params.date_to = filters.dateTo;
            if (searchTerm) params.search = searchTerm;

            const response = await api.get(APISystemLogsList, { params });
            const result = response.data;

            if (result.status === 'success') {
                setPageState(prev => ({
                    ...prev,
                    logsData: result.data || []
                }));
                setTotalCount(result.pagination?.total || 0);
            } else {
                showToast("Error", result.message || "Failed to load logs", "error");
            }
        } catch (err) {
            const parsedError = apiService.handleError(err, "Failed to load system logs");
            setError(parsedError.message);
            showToast("Error", parsedError.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistics = async () => {
        setStatsLoading(true);
        try {
            const userBranchId = getUserDetails("branchId");
            const currentUserId = getUserDetails("Id");

            if (!currentUserId) {
                setStatsLoading(false);
                return;
            }

            const params = { user_id: currentUserId };
            if (userBranchId) params.branch_id = userBranchId;

            const result = await apiService.listStats(userBranchId, APISystemLogsStats, params);
            
            if (result.success && result.data) {
                setPageState(prev => ({
                    ...prev,
                    statistics: result.data
                }));
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const currentUserId = getUserDetails("Id");
            const params = { user_id: currentUserId };
            const result = await apiService.listNames(APISystemLogsFilterOptions, params);
            if (result.success && result.data) {
                setPageState(prev => ({
                    ...prev,
                    filterOptions: result.data
                }));
            }
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    };

    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export logs", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            
            const currentUserId = getUserDetails("Id");
            if (!currentUserId) {
                showToast("Error", "User not authenticated", "error");
                return;
            }

            const formDataToSend = new FormData();
            formDataToSend.append('user_id', currentUserId);
            const branchId = getUserDetails("branchId");
            if (branchId) formDataToSend.append('branch_id', branchId);
            
            if (filters.model) formDataToSend.append('model', filters.model);
            if (filters.action) formDataToSend.append('action', filters.action);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToExcel(APISystemLogsExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            }
        } catch (error) {
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export logs", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const currentUserId = getUserDetails("Id");
            if (!currentUserId) {
                showToast("Error", "User not authenticated", "error");
                return;
            }

            const formDataToSend = new FormData();
            formDataToSend.append('user_id', currentUserId);
            const branchId = getUserDetails("branchId");
            if (branchId) formDataToSend.append('branch_id', branchId);
            formDataToSend.append('user_name', getUserDetails("userName"));
            
            if (filters.model) formDataToSend.append('model', filters.model);
            if (filters.action) formDataToSend.append('action', filters.action);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToPDF(APISystemLogsExportPDF, formDataToSend);
            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            }
        } catch (error) {
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenView = async (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedRecord(null);
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete logs", "error");
            return;
        }
        setSelectedRecord(record);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
        setSelectedRecord(null);
    };

    const handleDeleteLog = async () => {
        if (!hasPermission('DELETE')) return;

        const currentUserId = getUserDetails("Id");
        if (!currentUserId) {
            showToast("Error", "User not authenticated", "error");
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', currentUserId);
        const branchId = getUserDetails("branchId");
        if (branchId) formDataToSend.append('branch_id', branchId);
        formDataToSend.append('updateId', selectedRecord.id);

        try {
            const result = await apiService.delete(APISystemLogsDelete, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Log entry deleted successfully", "success");
                handleCloseDelete();
                fetchData();
                fetchStatistics();
            }
        } catch (error) {
            showToast("Error", "Failed to delete log entry", "error");
        }
    };

    const handleOpenCleanDialog = () => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to clean logs", "error");
            return;
        }
        setOpenCleanDialog(true);
    };

    const handleCloseCleanDialog = () => {
        setOpenCleanDialog(false);
        setCleanDays(30);
    };

    const handleCleanLogs = async () => {
        if (!hasPermission('DELETE')) return;

        const currentUserId = getUserDetails("Id");
        if (!currentUserId) {
            showToast("Error", "User not authenticated", "error");
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', currentUserId);
        const branchId = getUserDetails("branchId");
        if (branchId) formDataToSend.append('branch_id', branchId);
        formDataToSend.append('days', cleanDays.toString());

        try {
            const result = await apiService.create(APISystemLogsClean, formDataToSend);
            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseCleanDialog();
                fetchData();
                fetchStatistics();
            }
        } catch (error) {
            showToast("Error", "Failed to clean logs", "error");
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({ ...filters, dateFrom: today, dateTo: today });
        handleCloseOptions();
    };

    const applyThisWeekFilter = () => {
        const today = new Date();
        const weekAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).toISOString().split('T')[0];
        setFilters({ ...filters, dateFrom: weekAgo, dateTo: today.toISOString().split('T')[0] });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({ model: '', action: '', dateFrom: '', dateTo: '' });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => handleCloseOptions();

    const getActionColor = (action) => {
        const colors = {
            'create': 'success',
            'add': 'success',
            'store': 'success',
            'update': 'info',
            'edit': 'info',
            'modify': 'info',
            'delete': 'error',
            'remove': 'error',
            'destroy': 'error',
            'login': 'primary',
            'logout': 'secondary',
            'view': 'info',
            'read': 'info',
            'fetch': 'info',
            'print': 'warning',
            'export': 'warning'
        };
        return colors[action?.toLowerCase()] || 'default';
    };

    const getActionIcon = (action) => {
        const icons = {
            'create': <Add fontSize="small" />,
            'add': <Add fontSize="small" />,
            'store': <Add fontSize="small" />,
            'update': <Edit fontSize="small" />,
            'edit': <Edit fontSize="small" />,
            'modify': <Edit fontSize="small" />,
            'delete': <Delete fontSize="small" />,
            'remove': <Delete fontSize="small" />,
            'destroy': <Delete fontSize="small" />,
            'login': <Login fontSize="small" />,
            'logout': <Logout fontSize="small" />,
            'view': <Visibility fontSize="small" />,
            'read': <Visibility fontSize="small" />,
            'fetch': <Visibility fontSize="small" />,
            'print': <PrintOutlined fontSize="small" />,
            'export': <Download fontSize="small" />
        };
        return icons[action?.toLowerCase()] || <Info fontSize="small" />;
    };

    const getModelIcon = (model) => {
        return <Description fontSize="small" />;
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (permissionLoading) return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    
    if (!permissionLoading && !hasPermission('VIEW')) {
        return (
            <ErrorState 
                error="You do not have permission to view System Logs." 
                title="Access Denied" 
                variant="medical" 
                fullPage 
                showRetry={false}
            />
        );
    }

    if (loading && pageState.logsData.length === 0) return <LoadingState message="Loading System Logs..." variant="medical" size="large" fullPage />;
    if (error) return <ErrorState error={error} onRetry={fetchData} title="Unable to Load System Logs" variant="medical" fullPage retryText="Retry Loading" />;

    const stats = pageState.statistics;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2, width: 48, height: 48 }}>
                        <History sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                            System Logs
                        </Typography>
                        <Typography variant="body1" color={theme.palette.secondary.main}>
                            Monitor all system activities and user actions
                        </Typography>
                    </Box>
                </Box>
                <Chip 
                    icon={<Storage sx={{ fontSize: 20 }} />} 
                    label={`${totalCount} Total Records`} 
                    color="primary" 
                    variant="outlined" 
                    sx={{ py: 2, px: 1, fontSize: '0.95rem', fontWeight: 'bold' }}
                />
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={6} sm={3} md={2}>
                        <Card sx={{ textAlign: 'center', bgcolor: theme.palette.primary.light + '15', borderRadius: 2 }}>
                            <CardContent sx={{ py: 3 }}>
                                <Typography  sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                                    {statsLoading ? <LinearProgress /> : stats.total_logs}
                                </Typography>
                                <Typography variant="body2" color={theme.palette.secondary.main} sx={{ fontWeight: 'medium' }}>Total Logs</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <Card sx={{ textAlign: 'center', bgcolor: theme.palette.success.light + '15', borderRadius: 2 }}>
                            <CardContent sx={{ py: 3 }}>
                                <Typography  sx={{ fontWeight: 'bold', color: theme.palette.success.main, mb: 1 }}>
                                    {statsLoading ? <LinearProgress /> : stats.today_logs}
                                </Typography>
                                <Typography variant="body2" color={theme.palette.secondary.main} sx={{ fontWeight: 'medium' }}>Today</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <Card sx={{ textAlign: 'center', bgcolor: theme.palette.info.light + '15', borderRadius: 2 }}>
                            <CardContent sx={{ py: 3 }}>
                                <Typography  sx={{ fontWeight: 'bold', color: theme.palette.info.main, mb: 1 }}>
                                    {statsLoading ? <LinearProgress /> : stats.this_week_logs}
                                </Typography>
                                <Typography variant="body2" color={theme.palette.secondary.main} sx={{ fontWeight: 'medium' }}>This Week</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <Card sx={{ textAlign: 'center', bgcolor: theme.palette.warning.light + '15', borderRadius: 2 }}>
                            <CardContent sx={{ py: 3 }}>
                                <Typography  sx={{ fontWeight: 'bold', color: theme.palette.warning.main, mb: 1 }}>
                                    {statsLoading ? <LinearProgress /> : stats.this_month_logs}
                                </Typography>
                                <Typography variant="body2" color={theme.palette.secondary.main} sx={{ fontWeight: 'medium' }}>This Month</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Card sx={{ bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                            <CardContent sx={{ py: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>Recent Activity</Typography>
                                {stats.recent_activity?.slice(0, 3).map((activity, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                        <Chip 
                                            label={activity.action} 
                                            size="small" 
                                            color={getActionColor(activity.action)}
                                            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 'bold' }}
                                        />
                                        <Typography variant="body2" color={theme.palette.secondary.main} noWrap sx={{ maxWidth: '220px' }}>
                                            <strong>{activity.user_name}</strong> - {activity.created_at_human}
                                        </Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Search and Filter Bar */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, minWidth: '150px' }}>
                        Activity Logs
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={handleFilterOptions}
                            sx={{ textTransform: 'none', borderRadius: '8px', px: 2, py: 1, fontSize: '0.9rem' }}
                        >
                            Filters
                        </Button>

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px', px: 2, py: 1, fontSize: '0.9rem' }}
                            >
                                Export
                            </Button>
                        )}

                        {hasPermission('DELETE') && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<CleaningServices />}
                                onClick={handleOpenCleanDialog}
                                sx={{ textTransform: 'none', borderRadius: '8px', px: 2, py: 1, fontSize: '0.9rem' }}
                            >
                                Clean Logs
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => { fetchData(); fetchStatistics(); }}
                            sx={{ textTransform: 'none', borderRadius: '8px', px: 2, py: 1, fontSize: '0.9rem' }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: { xs: '100%', md: '320px' },
                        p: '4px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white', fontSize: 22 }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.95rem',
                                    color: 'white',
                                    '& input': { color: 'white', '&::placeholder': { color: 'rgba(255,255,255,0.75)', opacity: 1 } }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.model || filters.action || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color={theme.palette.secondary.main} sx={{ mr: 1, fontWeight: 'medium' }}>Active filters:</Typography>
                        {filters.model && (
                            <Chip label={`Model: ${filters.model}`} onDelete={() => setFilters(prev => ({ ...prev, model: '' }))} color="primary" variant="outlined" size="small" sx={{ fontSize: '0.8rem' }} />
                        )}
                        {filters.action && (
                            <Chip label={`Action: ${filters.action}`} onDelete={() => setFilters(prev => ({ ...prev, action: '' }))} color="secondary" variant="outlined" size="small" sx={{ fontSize: '0.8rem' }} />
                        )}
                        {filters.dateFrom && (
                            <Chip label={`From: ${filters.dateFrom}`} onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))} color="info" variant="outlined" size="small" sx={{ fontSize: '0.8rem' }} />
                        )}
                        {filters.dateTo && (
                            <Chip label={`To: ${filters.dateTo}`} onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))} color="info" variant="outlined" size="small" sx={{ fontSize: '0.8rem' }} />
                        )}
                        <Button size="small" onClick={clearFilters} startIcon={<CloseIcon />} sx={{ ml: 1, fontSize: '0.85rem' }}>Clear All</Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportClose}>
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1.5, fontSize: '0.95rem', py: 1 }}>
                    <TableChart fontSize="small" color="success" /> Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1.5, fontSize: '0.95rem', py: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" /> Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Logs Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
                        <Table stickyHeader size="medium">
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem',
                                        py: 1.5
                                    }
                                }}>
                                    <TableCell sx={{ width: 80 }}>ID</TableCell>
                                    <TableCell sx={{ width: 180 }}>Date & Time</TableCell>
                                    <TableCell sx={{ width: 220 }}>User</TableCell>
                                    <TableCell sx={{ width: 140 }}>Action</TableCell>
                                    <TableCell sx={{ width: 140 }}>Model</TableCell>
                                    <TableCell sx={{ width: 140 }}>Branch</TableCell>
                                    {/* <TableCell sx={{ width: 150 }}>IP Address</TableCell> */}
                                    <TableCell>Notes</TableCell>
                                    <TableCell align="center" sx={{ width: 110 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pageState.logsData.map((log) => (
                                    <TableRow key={log.id} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}>
                                        <TableCell>
                                            <Chip label={log.id} size="small" color="primary" variant="outlined" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" display="block" sx={{ fontWeight: 'bold' }}>
                                                    {log.created_date}
                                                </Typography>
                                                <Typography variant="body2" color={theme.palette.secondary.main} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontSize: '0.8rem' }}>
                                                    <AccessTime sx={{ fontSize: 13 }} />
                                                    {log.created_time}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.85rem', bgcolor: theme.palette.primary.main }}>
                                                    {log.user_name?.charAt(0) || 'S'}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" display="block" sx={{ fontWeight: 'bold' }}>
                                                        {log.user_name}
                                                    </Typography>
                                                    <Typography variant="body2" color={theme.palette.secondary.main} sx={{ fontSize: '0.8rem' }}>
                                                        {log.user_email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getActionIcon(log.action)}
                                                label={log.action?.toUpperCase()}
                                                size="small"
                                                color={getActionColor(log.action)}
                                                sx={{ fontWeight: 'bold', fontSize: '0.75rem', px: 0.5 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getModelIcon(log.model)}
                                                label={log.model}
                                                size="small"
                                                variant="outlined"
                                                color="secondary"
                                                sx={{ fontSize: '0.8rem' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{log.branch_name}</Typography>
                                        </TableCell>
                                        {/* <TableCell>
                                            <Chip
                                                icon={<Computer sx={{ fontSize: 14 }} />}
                                                label={log.ip_address}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.8rem' }}
                                            />
                                        </TableCell> */}
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 220, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {log.notes || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => handleOpenView(log)} color="primary">
                                                        <ViewIcon fontSize="medium" />
                                                    </IconButton>
                                                </Tooltip>
                                                {hasPermission('DELETE') && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleOpenDelete(log)} color="error">
                                                            <Delete fontSize="medium" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {pageState.logsData.length === 0 && !loading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, color: 'text.secondary' }}>
                            <History sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No logs found</Typography>
                            <Typography variant="body2">Try adjusting your filters or search terms</Typography>
                        </Box>
                    )}

                    <TablePagination
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ fontSize: '0.9rem' }}
                    />
                </Paper>
            </motion.div>

            {/* View Dialog */}
            <Dialog open={openView} onClose={handleCloseView} TransitionComponent={Transition} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.25rem', py: 2.5 }}>
                    <Computer sx={{ verticalAlign: 'middle', mr: 1, fontSize: 24 }} />
                    Log Entry Details
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {selectedRecord && (
                        <Box>
                            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: theme.palette.grey[100], borderRadius: 2 }}>
                                <Box>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>Log ID :&nbsp;&nbsp;&nbsp;&nbsp; #{selectedRecord.id}</Typography>
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>Date & Time :&nbsp;&nbsp;&nbsp;&nbsp; {selectedRecord.created_at}</Typography>
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>User : &nbsp;&nbsp;&nbsp;&nbsp;{selectedRecord.user_name}</Typography>
                                    </Grid><br/>
                                    {/* <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>IP Address : &nbsp;&nbsp;&nbsp;&nbsp;
                                            <Chip icon={<Computer />} label={selectedRecord.ip_address} size="small" variant="outlined" sx={{ fontSize: '0.85rem' }} />
                                        </Typography>
                                    </Grid> */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>Action : &nbsp;&nbsp;&nbsp;&nbsp;
                                            <Chip icon={getActionIcon(selectedRecord.action)} label={selectedRecord.action?.toUpperCase()} size="small" color={getActionColor(selectedRecord.action)} sx={{ fontSize: '0.8rem', fontWeight: 'bold' }} />
                                        </Typography>
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>Model : &nbsp;&nbsp;&nbsp;&nbsp;
                                            <Chip label={selectedRecord.model} size="small" variant="outlined" color="secondary" sx={{ fontSize: '0.85rem' }} />
                                        </Typography>
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>Branch : &nbsp;&nbsp;&nbsp;&nbsp;{selectedRecord.branch_name || '—'}</Typography>
                                    </Grid><br/>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color={theme.palette.secondary.main} sx={{ fontWeight: 'bold' }}>Relative Time : &nbsp;&nbsp;&nbsp;&nbsp;{selectedRecord.created_at_human}</Typography>
                                    </Grid>
                                </Box>
                            </Paper>
                            
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Notes</Typography>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: theme.palette.warning.light + '15', borderRadius: 2, minHeight: '80px' }}>
                                <Typography variant="body1">{selectedRecord.notes || 'No additional notes'}</Typography>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseView} color="primary" variant="contained" sx={{ px: 3 }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={openDelete} onClose={handleCloseDelete} TransitionComponent={Transition}>
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>Delete Log Entry</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 3 }}>
                        Are you sure you want to delete this log entry?
                    </Typography>
                    {selectedRecord && (
                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Log #{selectedRecord.id}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                <Chip icon={getActionIcon(selectedRecord.action)} label={selectedRecord.action?.toUpperCase()} color={getActionColor(selectedRecord.action)} size="small" />
                                <Chip label={selectedRecord.model} variant="outlined" size="small" />
                            </Box>
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 3, fontWeight: 'medium' }}>
                        This action cannot be undone!
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button color="primary" onClick={handleCloseDelete} variant="outlined">Cancel</Button>
                    <Button onClick={handleDeleteLog} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Clean Logs Dialog */}
            <Dialog open={openCleanDialog} onClose={handleCloseCleanDialog} TransitionComponent={Transition} fullWidth maxWidth="xs">
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    <CleaningServices sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Clean Old Logs
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="warning" sx={{ mb: 3, fontSize: '0.9rem' }}>
                            This will permanently delete log entries older than the specified number of days.
                        </Alert>
                        <TextField
                            fullWidth
                            label="Delete logs older than (days)"
                            type="number"
                            value={cleanDays}
                            onChange={(e) => setCleanDays(e.target.value)}
                            InputProps={{
                                inputProps: { min: 1, max: 365 },
                                startAdornment: <InputAdornment position="start"><CalendarMonth /></InputAdornment>
                            }}
                        />
                        <Typography variant="body2" color={theme.palette.secondary.main} sx={{ mt: 1.5, display: 'block' }}>
                            Recommended: 30-90 days for regular cleanup
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseCleanDialog} color="primary" variant="outlined">Cancel</Button>
                    <Button onClick={handleCleanLogs} color="error" variant="contained" startIcon={<DeleteSweep />}>
                        Clean Logs
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Filter Options Dialog */}
            <Dialog open={filterOptions} TransitionComponent={Transition} fullWidth maxWidth="sm" onClose={handleCloseOptions}>
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>Filter Options</DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={clearFilters} sx={{ textTransform: 'none', py: 1, fontSize: '0.95rem' }}>
                            Show All Logs
                        </Button>
                        <Button variant="outlined" color="primary" startIcon={<TodayIcon />} onClick={applyTodayFilter} sx={{ textTransform: 'none', py: 1, fontSize: '0.95rem' }}>
                            Today's Logs
                        </Button>
                        <Button variant="outlined" color="primary" startIcon={<CalendarMonth />} onClick={applyThisWeekFilter} sx={{ textTransform: 'none', py: 1, fontSize: '0.95rem' }}>
                            This Week's Logs
                        </Button>
                        
                        <Divider sx={{ my: 1 }}><Typography variant="body2" color={theme.palette.secondary.main} sx={{ px: 1 }}>OR FILTER BY FIELDS</Typography></Divider>
                        
                        <FormControl fullWidth>
                            <InputLabel>Model</InputLabel>
                            <Select name="model" value={filters.model} onChange={handleFilterChange} label="Model">
                                <MenuItem value="">All Models</MenuItem>
                                {pageState.filterOptions.models?.map((model) => (
                                    <MenuItem key={model} value={model}>{model}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Action</InputLabel>
                            <Select name="action" value={filters.action} onChange={handleFilterChange} label="Action">
                                <MenuItem value="">All Actions</MenuItem>
                                {pageState.filterOptions.actions?.map((action) => (
                                    <MenuItem key={action} value={action}>{action}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider sx={{ my: 1 }}><Typography variant="body2" color={theme.palette.secondary.main} sx={{ px: 1 }}>OR DATE RANGE</Typography></Divider>
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="From Date" type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="To Date" type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">Apply Filters</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default SystemLogs;
